import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const useDashboardData = () => {
  const { user, api } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [liveEvent, setLiveEvent] = useState(null);
  const [showLivePopup, setShowLivePopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [topContributors, setTopContributors] = useState([]);
  const [tutorialCompleted, setTutorialCompleted] = useState(true);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const response = await api.get(`/badges/user/${user.id}`);
      setBadges(response.data);
    } catch (error) {
      console.error('Badges error:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications/smart');
      const list = response.data?.notifications || [];
      const unread = response.data?.unread_count ?? list.filter(n => !(n.read || n.is_read)).length;
      setUnreadNotifications(unread);
    } catch (error) {
      console.error('Notifications error:', error);
    }
  };

  const fetchTopContributors = async () => {
    try {
      const response = await api.get('/leaderboard?limit=3');
      setTopContributors(response.data.slice(0, 3));
    } catch (error) {
      console.error('Leaderboard error:', error);
    }
  };

  const checkTutorialStatus = async () => {
    try {
      const response = await api.get('/tutorial/status');
      setTutorialCompleted(response.data.completed);
    } catch (error) {
      setTutorialCompleted(false);
    }
  };

  const handleTutorialComplete = async () => {
    try {
      await api.post('/tutorial/complete');
      setTutorialCompleted(true);
    } catch (error) {
      console.error('Error completing tutorial:', error);
    }
  };

  const checkLocationPrompt = async () => {
    // Ne pas afficher si le tutoriel n'est pas terminé
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    if (!onboardingCompleted) {
      return;
    }
    
    try {
      const response = await api.get('/auth/profile');
      if (!response.data.location_prompted && !response.data.show_location) {
        setShowLocationPrompt(true);
      }
    } catch (error) {
      console.error('Location check error:', error);
    }
  };

  const handleLocationPromptClose = async (goToMap = false) => {
    try {
      await api.post('/members/location-prompted');
    } catch (error) {
      console.error('Error marking location prompted:', error);
    }
    setShowLocationPrompt(false);
    return goToMap;
  };

  const checkLiveEvent = useCallback(async () => {
    try {
      const response = await api.get('/events/live');
      if (response.data && response.data.id && (!liveEvent || liveEvent.id !== response.data.id)) {
        setLiveEvent(response.data);
        setShowLivePopup(true);
      } else if (!response.data || !response.data.id) {
        setLiveEvent(null);
        setShowLivePopup(false);
      }
    } catch (error) {
      // Silently fail
    }
  }, [liveEvent]);

  useEffect(() => {
    fetchDashboard();
    fetchBadges();
    checkLiveEvent();
    checkLocationPrompt();
    fetchNotifications();
    fetchTopContributors();
    checkTutorialStatus();
    const interval = setInterval(checkLiveEvent, 60000);
    return () => clearInterval(interval);
  }, []);

  const earnedBadges = badges.filter(b => b.earned);

  return {
    dashboardData,
    liveEvent, setLiveEvent,
    showLivePopup, setShowLivePopup,
    loading,
    badges,
    earnedBadges,
    unreadNotifications,
    topContributors,
    tutorialCompleted,
    handleTutorialComplete,
    showLocationPrompt,
    handleLocationPromptClose,
  };
};

export default useDashboardData;
